import { jsPDF } from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';

const MM_PER_INCH = 25.4;
const PNG_DPI = 300;

const SVG_STYLE_PROPERTIES = [
  'color',
  'fill',
  'fill-opacity',
  'filter',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'letter-spacing',
  'opacity',
  'paint-order',
  'shape-rendering',
  'stop-color',
  'stop-opacity',
  'stroke',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-opacity',
  'stroke-width',
  'text-anchor',
  'vector-effect',
  'visibility',
];

const inlineComputedStyles = (sourceSvg, clonedSvg) => {
  const sourceElements = [sourceSvg, ...sourceSvg.querySelectorAll('*')];
  const clonedElements = [clonedSvg, ...clonedSvg.querySelectorAll('*')];

  sourceElements.forEach((sourceElement, index) => {
    const clonedElement = clonedElements[index];
    if (!clonedElement) return;
    const computed = window.getComputedStyle(sourceElement);
    SVG_STYLE_PROPERTIES.forEach(property => {
      const value = computed.getPropertyValue(property);
      if (value) clonedElement.style.setProperty(property, value);
    });
  });
};

export const serializeLayoutSvg = (sourceSvg, canvas) => {
  if (!sourceSvg) throw new Error('Layout canvas is not available');

  const clonedSvg = sourceSvg.cloneNode(true);
  inlineComputedStyles(sourceSvg, clonedSvg);
  clonedSvg.querySelectorAll('[data-layout-export-exclude="true"]').forEach(element => element.remove());
  clonedSvg.querySelectorAll('[data-layout-export-background="true"]').forEach(element => {
    element.setAttribute('fill', '#ffffff');
    element.style.setProperty('fill', '#ffffff');
  });
  clonedSvg.querySelectorAll('.layout-conductor-line').forEach(element => {
    element.style.setProperty('filter', 'none');
    const strokeWidth = element.getAttribute('data-layout-export-stroke-width');
    if (strokeWidth) {
      element.setAttribute('stroke-width', strokeWidth);
      element.style.setProperty('stroke-width', strokeWidth);
    }
  });

  clonedSvg.setAttribute('width', `${canvas.width}mm`);
  clonedSvg.setAttribute('height', `${canvas.height}mm`);
  clonedSvg.style.removeProperty('width');
  clonedSvg.style.removeProperty('height');
  clonedSvg.setAttribute('viewBox', `0 0 ${canvas.width} ${canvas.height}`);
  clonedSvg.setAttribute('data-layout-export', 'true');
  clonedSvg.removeAttribute('role');
  clonedSvg.removeAttribute('aria-label');

  return new XMLSerializer().serializeToString(clonedSvg);
};

const exportFilename = (title, extension) => {
  const basename = String(title || 'amplifier-layout')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-') || 'amplifier-layout';
  return `${basename}.${extension}`;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const rasterizeLayoutSvg = (svg, canvas, dpi = PNG_DPI) => new Promise((resolve, reject) => {
  const image = new Image();
  const sourceUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  image.onload = () => {
    try {
      const raster = document.createElement('canvas');
      raster.width = Math.round(canvas.width / MM_PER_INCH * dpi);
      raster.height = Math.round(canvas.height / MM_PER_INCH * dpi);
      const context = raster.getContext('2d');
      if (!context) throw new Error('Canvas rendering is not available');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, raster.width, raster.height);
      context.drawImage(image, 0, 0, raster.width, raster.height);
      resolve(raster);
    } catch (error) {
      reject(error);
    } finally {
      URL.revokeObjectURL(sourceUrl);
    }
  };
  image.onerror = () => {
    URL.revokeObjectURL(sourceUrl);
    reject(new Error('Unable to render the layout SVG'));
  };
  image.src = sourceUrl;
});

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob(blob => {
    if (blob) resolve(blob);
    else reject(new Error('Unable to encode the layout image'));
  }, 'image/png');
});

const createPhysicalPdf = (canvas) => {
  const orientation = canvas.width > canvas.height ? 'landscape' : 'portrait';
  return new jsPDF({ orientation, unit: 'mm', format: [canvas.width, canvas.height] });
};

export const exportLayoutFile = async (sourceSvg, canvas, format, title) => {
  const svg = serializeLayoutSvg(sourceSvg, canvas);
  if (format === 'svg') {
    downloadBlob(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }), exportFilename(title, 'svg'));
    return;
  }

  if (format === 'pdf-vector') {
    const svgElement = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
    const pdf = createPhysicalPdf(canvas);
    await svg2pdf(svgElement, pdf, { x: 0, y: 0, width: canvas.width, height: canvas.height });
    pdf.save(exportFilename(title, 'pdf'));
    return;
  }

  const raster = await rasterizeLayoutSvg(svg, canvas);
  if (format === 'png') {
    downloadBlob(await canvasToBlob(raster), exportFilename(title, 'png'));
    return;
  }

  if (format === 'pdf') {
    const pdf = createPhysicalPdf(canvas);
    pdf.addImage(raster.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, canvas.width, canvas.height);
    pdf.save(exportFilename(title, 'pdf'));
    return;
  }

  throw new Error(`Unsupported layout export format: ${format}`);
};
